import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfigProvider, useConfig } from './context/ConfigContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Agenda from './pages/Agenda';
import Speakers from './pages/Speakers';
import Streaming from './pages/Streaming';
import Profile from './pages/Profile';
import Scanner from './pages/Scanner';
import SelectSala from './pages/SelectSala';
import GalleryPage from './pages/GalleryPage';
import StaffQuestions from './pages/StaffQuestions';
import StaffGallery from './pages/StaffGallery';
import EventGallery from './pages/EventGallery';
import WelcomeLetter from './pages/WelcomeLetter';
import ThankYouLetter from './pages/ThankYouLetter';
import Certificate from './pages/Certificate';
import UserQuestions from './pages/UserQuestions';
import InteractivePoll from './pages/InteractivePoll';
import StaffPollManagement from './pages/StaffPollManagement';
import StaffPollResults from './pages/StaffPollResults';
import Survey from './pages/Survey';
import DynamicPage from './pages/DynamicPage';

// Protected Route Component
const ProtectedRoute = ({ children, pageKey, allowRoles }) => {
    const { user, loading: authLoading } = useAuth();
    const { config, loading: configLoading } = useConfig();

    if (authLoading || configLoading) return (
        <div className="h-screen w-full flex flex-col items-center justify-center bg-white">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <span className="font-black text-[10px] uppercase tracking-[0.3em] text-slate-300"> Loading</span>
        </div>
    );

    if (!user) return <Navigate to="/login" />;

    // Check if the page is disabled in config
    if (pageKey && config?.pages?.[pageKey] === false) {
        return <Navigate to="/" />;
    }

    // Role check
    if (allowRoles && !allowRoles.includes(user.role)) {
        return <Navigate to="/" />;
    }

    return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
    const { config } = useConfig();

    return (
        <Router>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/survey" element={<Layout><Survey /></Layout>} />

                {/* Protected Routes */}
                <Route path="/" element={<ProtectedRoute pageKey="home"><Home /></ProtectedRoute>} />
                <Route path="/agenda" element={<ProtectedRoute pageKey="agenda"><Agenda /></ProtectedRoute>} />
                <Route path="/speakers" element={<ProtectedRoute pageKey="speakers"><Speakers /></ProtectedRoute>} />
                <Route path="/streaming" element={<ProtectedRoute pageKey="streaming"><Streaming /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute pageKey="profile"><Profile /></ProtectedRoute>} />
                <Route path="/seleccionar-sala" element={<ProtectedRoute pageKey="sala"><SelectSala /></ProtectedRoute>} />
                <Route path="/scanner" element={<ProtectedRoute allowRoles={['staff', 'admin']}><Scanner /></ProtectedRoute>} />
                <Route path="/staff-questions" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffQuestions /></ProtectedRoute>} />
                <Route path="/staff-gallery" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffGallery /></ProtectedRoute>} />
                <Route path="/event-gallery" element={<ProtectedRoute><EventGallery /></ProtectedRoute>} />
                <Route path="/welcome" element={<ProtectedRoute pageKey="welcome"><WelcomeLetter /></ProtectedRoute>} />
                <Route path="/thanks" element={<ProtectedRoute pageKey="thanks"><ThankYouLetter /></ProtectedRoute>} />
                <Route path="/certificate" element={<ProtectedRoute pageKey="certificate"><Certificate /></ProtectedRoute>} />
                <Route path="/questions-expert" element={<ProtectedRoute pageKey="questions_private"><UserQuestions /></ProtectedRoute>} />
                <Route path="/interactiva" element={<ProtectedRoute><InteractivePoll /></ProtectedRoute>} />
                <Route path="/interactiva-gestion" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffPollManagement /></ProtectedRoute>} />
                <Route path="/interactiva-stats/:id" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffPollResults /></ProtectedRoute>} />

                {/* Dynamic Gallery Routes */}
                {config?.customGalleryPages?.map(page => (
                    <Route
                        key={page.slug}
                        path={`/gallery/${page.slug}`}
                        element={<ProtectedRoute><GalleryPage /></ProtectedRoute>}
                    />
                ))}

                {/* Custom CMS Pages */}
                <Route path="/p/:slug" element={<ProtectedRoute><DynamicPage /></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </Router>
    );
};

function App() {
    return (
        <ConfigProvider>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </ConfigProvider>
    );
}

export default App;
